import { Autocomplete, Box, TextField } from '@mui/material';
import TextInput from 'components/Common/TextInput';
import Button from 'components/Common/Button';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, store } from 'redux/store';
import { userUpdateInfo } from 'redux/slices/user/userSlice';
import countries from 'Data/countries.json';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import routes from 'constants/routes';
import CardWrapper from './CardWrapper';
import { UpdateUserDataPayload } from 'types/user';

interface EditContainerProps {
    children?: React.ReactNode;
    type: 'account' | 'staff'; // Add the 'type' prop
    defaultValues?: any;
}
// intialised form values that are required by the backend but not designed in figma
const initialFormValues = {
    telephoneType: 'CELLPHONE',
    address: {
        addressType: 'HOME',
        name: 'job',
        isDefault: true,
        type: 'HOME',
    },
};
const formSchema = yup.object({
    telephone: yup.string().required('required contact number'),
    address: yup.object({
        address1: yup.string().required('Address1 is required.'),
        city: yup.string().required('City is required.'),
        state: yup.string().required('State is required.'),
        country: yup.string().required('Country is required.'),
        zip: yup.string().required('ZipCode is required.'),
    }),
});
function EditContainer({ children, type, defaultValues }: EditContainerProps) {
    const navigate = useNavigate();
    const { updateInfoLoading, isMerchant, merchant } = useSelector((state: RootState) => ({
        updateInfoLoading: state.user?.updateInfoLoading,
        isMerchant: state.auth?.isMerchant,
        merchant: state.user.defaultMerchant,
    }));
    const dispatch = useDispatch<typeof store.dispatch>();
    const isAccountType = type === 'account';
    const address = { ...initialFormValues?.address, ...defaultValues?.address };
    const parishName = defaultValues?.parishName || merchant?.name;
    const {
        handleSubmit,
        setValue,
        watch,
        control,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: { ...initialFormValues, ...defaultValues, address, parishName },
    });

    const onSubmit = async (formValues: UpdateUserDataPayload) => {
        const res: any = await dispatch(userUpdateInfo(formValues));
        const { payload } = res || {};
        if (payload?.code === 200) {
            navigate(routes.account.main);
        }
    };
    const getCountry = (code: string) => {
        return countries.find((c) => c.code === code);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <CardWrapper sx={{ my: 6 }}>
                {children}
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                    <TextInput
                        name={'firstName'}
                        value={watch('firstName')}
                        sx={{ mt: 2, mb: 2 }}
                        width="49%"
                        label={isMerchant ? 'Account Owner First Name' : 'First Name'}
                        placeholder="Enter your first name"
                        icon={null}
                        disabled
                    />
                    <TextInput
                        name={'lastName'}
                        value={watch('lastName')}
                        sx={{ mt: 2, mb: 2 }}
                        width="49%"
                        placeholder="Enter your last name"
                        iconPosition="end"
                        label={isMerchant ? 'Account Owner Last Name' : 'Last Name'}
                        icon={null}
                        disabled
                    />
                </div>
                <TextInput name={'email'} value={watch('email')} sx={{ mt: 2, mb: 2 }} width="100%" label="Email" icon={null} disabled />
                {isMerchant && (
                    <>
                        <TextInput
                            sx={{ mt: 2, mb: 2 }}
                            width="100%"
                            placeholder="Catholic Directory account number"
                            label="Catholic Directory account number"
                            icon={null}
                            value={watch('catholicDirectoryAccountNumber')}
                            name="catholicDirectoryAccountNumber"
                            setValue={setValue}
                        />
                        <TextInput
                            name={'username'}
                            value={watch('username')}
                            setValue={setValue}
                            sx={{ mt: 2, mb: 2 }}
                            width="100%"
                            label="Username"
                            placeholder="userName"
                            icon={null}
                        />
                        <TextInput
                            sx={{ mt: 2, mb: 2 }}
                            width="100%"
                            label="Parish Name"
                            icon={null}
                            value={watch('parishName')}
                            name="parishName"
                            setValue={setValue}
                        />
                    </>
                )}
                <Controller
                    name="telephone"
                    control={control}
                    render={({ field }) => (
                        <TextInput
                            {...field}
                            sx={{ mt: 2, mb: 2 }}
                            width="100%"
                            label="Contact Number"
                            placeholder="Contact Number"
                            icon={null}
                            error={!!errors.telephone}
                            helperText={(errors?.telephone as any)?.message}
                        />
                    )}
                />
                <Controller
                    name="address.address1"
                    control={control}
                    render={({ field }) => (
                        <TextInput
                            {...field}
                            sx={{ mt: 2, mb: 2 }}
                            width="100%"
                            label="Address1"
                            placeholder="Address1"
                            icon={null}
                            error={!!(errors?.address as any)?.address1}
                            helperText={(errors?.address as any)?.address1?.message}
                        />
                    )}
                />
                <TextInput
                    sx={{ mt: 2, mb: 2 }}
                    width="100%"
                    label="Address2"
                    placeholder="Address2"
                    icon={null}
                    name={'address.address2'}
                    value={watch('address.address2')}
                    setValue={setValue}
                />
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Controller
                        name="address.city"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                sx={{ mt: 2, mb: 2 }}
                                width="49%"
                                label="City"
                                placeholder="City"
                                icon={null}
                                error={!!(errors.address as any)?.city}
                                helperText={(errors.address as any)?.city?.message}
                            />
                        )}
                    />

                    <Controller
                        name="address.state"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                sx={{ mt: 2, mb: 2 }}
                                width="49%"
                                label="State"
                                placeholder="State"
                                icon={null}
                                error={!!(errors.address as any)?.state}
                                helperText={(errors.address as any)?.state?.message}
                            />
                        )}
                    />
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Controller
                        name="address.zip"
                        control={control}
                        render={({ field }) => (
                            <>
                                <TextInput
                                    {...field}
                                    sx={{ mt: 2, mb: 2 }}
                                    width="49%"
                                    label="Zip code"
                                    placeholder="Zip code"
                                    icon={null}
                                    error={!!(errors.address as any)?.zip}
                                    helperText={(errors.address as any)?.zip?.message}
                                />
                            </>
                        )}
                    />
                    <Controller
                        name="address.country"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                {...field}
                                id="address-country-select"
                                options={countries}
                                sx={{ width: '49%', mt: 2, mb: 2 }}
                                getOptionLabel={(option) => option.name}
                                value={getCountry(watch('address.country'))}
                                onChange={(event, value) => {
                                    setValue('address.country', value?.code || '');
                                    field.onChange(value?.code);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Country"
                                        error={!!(errors?.address as any)?.country}
                                        helperText={(errors?.address as any)?.country ? (errors?.address as any)?.country?.message : ''}
                                    />
                                )}
                            />
                        )}
                    />
                </div>
                {/* {!isMerchant && (
                <Box sx={{mb:10, mt:5}}>
                    <EmployeeLevelFooter/>
                </Box>
            )} */}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: !isAccountType ? 12 : 26 }}>
                    <Button loading={updateInfoLoading} type="submit" sx={{ width: { xs: '80%', sm: '50%' } }} title="Save" />
                </div>
            </CardWrapper>
        </form>
    );
}

export default EditContainer;
